// Kinio Station / Kinio Node 只開放給指定品牌（SPEC #catalog-kinio-family-brands，Eddy 2026-10-07）。
// 清單在平台設定（infra/config catalog.kinio_family），VMS 啟動時從公開 API 讀，不各自寫死：
//   GET {plane api}/v1/catalog/kinio-family → {"allowed_brands":[…],"model_globs":[…]}
// 拿不到（沒有該 env 的 plane、404、網路錯、逾時、格式不對）→ 用預設值（同平台設定的預設）。
// 開放清單以外的品牌：VMS 不出現 Station / Node 的入口（新增 Station、相關頁籤）；平台另外擋配對（422 MODEL_NOT_AVAILABLE_FOR_BRAND）。
import { planeBase } from '@kinio/web-sdk';
import type { AppConfig } from './appConfig';

export interface KinioFamily {
  allowed_brands: string[];
  model_globs: string[];
}

export const KINIO_FAMILY_DEFAULT: KinioFamily = Object.freeze({
  allowed_brands: ['kinio', 'livingstyle'],
  model_globs: ['kinio-station-*', 'kinio-node-*'],
}) as KinioFamily;

export const KINIO_FAMILY_TIMEOUT_MS = 2000;

/** Kinio Station 的 AID 型態碼（SPEC #vms-web-s5-19-1：TYPE KST 是 Kinio Station 專用）。
 *  配對時還不知道機種，只有 AID；Kinio Node 是 GEN（不專屬），配對時靠平台擋。 */
const STATION_TYPE_CODE = 'KST';

const strList = (v: unknown): string[] | null =>
  Array.isArray(v) && v.every((x) => typeof x === 'string') ? v.map((x) => x.trim()).filter(Boolean) : null;

/** 問 planes[env] 的 GET /v1/catalog/kinio-family（不帶 token、逾時 2 秒）；任何失敗都回預設值。 */
export async function fetchKinioFamily(
  cfg: Pick<AppConfig, 'env' | 'planes'>,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = KINIO_FAMILY_TIMEOUT_MS,
): Promise<KinioFamily> {
  const api = cfg.planes?.[cfg.env];
  if (!api) return KINIO_FAMILY_DEFAULT;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetchImpl(`${planeBase(api)}/v1/catalog/kinio-family`, {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      signal: ctl.signal,
    });
    if (!res.ok) return KINIO_FAMILY_DEFAULT;
    const body = (await res.json()) as { allowed_brands?: unknown; model_globs?: unknown } | null;
    const brands = strList(body?.allowed_brands);
    const globs = strList(body?.model_globs);
    if (!brands || !globs) return KINIO_FAMILY_DEFAULT;
    return { allowed_brands: brands.map((b) => b.toLowerCase()), model_globs: globs };
  } catch {
    return KINIO_FAMILY_DEFAULT;
  } finally {
    clearTimeout(timer);
  }
}

/** 只支援 `*`（任意字元，含空字串）；其他字元照字面比，不分大小寫。 */
export function globMatch(glob: string, s: string): boolean {
  const re = glob
    .split('*')
    .map((p) => p.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*');
  return new RegExp(`^${re}$`, 'i').test(s);
}

/** 這個機種是 Kinio Station / Kinio Node（符合 model_globs）。 */
export function isKinioFamilyModel(model: string | undefined, fam: KinioFamily = KINIO_FAMILY_DEFAULT): boolean {
  return !!model && fam.model_globs.some((g) => globMatch(g, model));
}

/** 這個品牌有開放 Kinio Station / Kinio Node。 */
export function kinioFamilyOpen(brand: string | undefined, fam: KinioFamily = KINIO_FAMILY_DEFAULT): boolean {
  return !!brand && fam.allowed_brands.includes(brand.toLowerCase());
}

/** 品牌沒開放、而且機種是 Station / Node → 介面不列。 */
export function hideKinioFamilyModel(brand: string | undefined, model: string | undefined, fam: KinioFamily = KINIO_FAMILY_DEFAULT): boolean {
  return !kinioFamilyOpen(brand, fam) && isKinioFamilyModel(model, fam);
}

/** 配對（只有 AID）：品牌沒開放、而且 AID 是 Kinio Station（KST）→ 不讓開始配對。 */
export function blockKinioFamilyAid(brand: string | undefined, aid: string, fam: KinioFamily = KINIO_FAMILY_DEFAULT): boolean {
  return !kinioFamilyOpen(brand, fam) && aid.trim().toUpperCase().startsWith(`${STATION_TYPE_CODE}-`);
}

/** 啟動時讀到的開放清單放進 config（useApp().config.kinio_family）。 */
export function withKinioFamily(cfg: AppConfig, fam: KinioFamily): AppConfig {
  return { ...cfg, kinio_family: fam };
}
